/**
 * Juice constants — render-side feel tuning.
 *
 * types.ts and tuning.ts are Kimi-owned and must not be modified. Any new
 * feel constant introduced for Stage 2 juice lives here. All values are
 * render-side only; they never affect sim behavior.
 *
 * Units: seconds for durations, degrees for angles, metres for distances
 * unless noted.
 */
export const JUICE = {
  // --- Screen shake ---------------------------------------------------------
  shake: {
    kickRotDeg: 1.8, // peak rotational shake magnitude on kick connect
    kickDuration: 0.15, // s — decays to zero
    // The crash shake is an IMPACT shake: it fires on the crash frame and is
    // completely gone `crashDuration` later. There is deliberately no
    // sustained "tumble" oscillation — the ragdoll keeps tumbling for the rest
    // of the 'crashed' phase, but the camera holds perfectly still while it
    // does, so the fall reads clearly instead of the shake cutting out at the
    // arbitrary moment the body happens to settle.
    crashRotDeg: 5.5, // peak rotational shake on crash
    crashPitchDeg: 3.0, // additional pitch jitter on crash
    crashDuration: 0.5, // s — hard stop; no shake after this
    crashDecayExp: 2, // (1 - t/duration)^exp — quadratic ease to a still camera
    noiseFreq: 18, // Hz of the perlin-ish noise driving shake
    // Smooth damping so shake does not fight the strafe roll (applied
    // additively AFTER rig.update, so the rig's own roll is untouched).
  },

  // --- FOV punch ------------------------------------------------------------
  fovPunch: {
    kickDeg: 5.0, // +fov spike on kick (on top of speed FOV)
    kickDuration: 0.12, // s — out-and-back
  },

  // --- Hitstop visual -------------------------------------------------------
  hitstop: {
    // Applied only while TUNING.kick.hitstop > 0. That constant is currently 0
    // (the launch is instant), so HitstopGate arms a zero-length window and this
    // zoom is never reached — it comes back automatically if hitstop is raised.
    zoomFovDeg: -3.0, // brief FOV decrease (zoom-in) during sim hitstop
  },

  // --- Kick leg animation ---------------------------------------------------
  // Timeline: extend / HOLD / retract. The hold is what makes the kick
  // readable — without it the leg is a one-frame blur. Total must stay well
  // under the sim's kick cooldown so pokes never overlap.
  leg: {
    loadPath: "/assets/models/leg_rig.glb",
    /**
     * s — extend + hold + retract (<< the kick cooldown).
     *
     * SNAP out, hold, COME HOME SLOWLY. The three phases are pinned in
     * ABSOLUTE seconds and the fractions below are derived from them, because
     * the design constraint is a ratio between two phase DURATIONS:
     *
     *   extend  (chamber + thrust — "the kick" itself)   0.1045 s  UNCHANGED
     *   hold    (full extension, readability)            0.1650 s  UNCHANGED
     *   retract                                          1.6000 s  (was 0.8)
     *
     * The retract used to be 2.68x the extend ("~2.7× faster out than home").
     * Owner playtest reversed the snappy-retract decision three times: first
     * to "3x slower than the kick" — which, read literally against the
     * 0.1045 s extend, gives 0.3135 s, a wall-clock change of only 12% that
     * would not read at all — so 0.8 s (3x the whole visible STRIKE,
     * extend + hold ≈ 0.27 s) shipped; the 2026-08-02 playtest then asked for
     * "2x longer" again, so the retract is now 1.6 s: the leg drifts home in
     * long, unmistakable slow-motion against the snap out. Still under the
     * 5 s kick cooldown (total 1.87 s). Only the retract moved —
     * chamber/thrust untouched in wall-clock terms — and every retract
     * sub-timing (retractDropExp/TiltExp/ReachExp, retractFoldFrac,
     * retractFadeFrac) is a FRACTION of the retract span, so the
     * flatten/fold/alpha machinery that keeps the leg off the road plane
     * stretches with it and the pose curve itself (and therefore the pinned
     * floating-island metric in juice.test.ts) is bit-for-bit unchanged.
     */
    pokeDuration: 0.1045 + 0.165 + 1.6, // 1.8695 s
    pokeExtendFrac: 0.1045 / 1.8695, // 0.0559 — 104.5 ms of SNAP (easeOut)
    pokeHoldFrac: 0.165 / 1.8695, // 0.0883 — 165 ms at FULL extension
    // --- UNUSED BY THE WHIFF PATH (kept for reference / re-tuning) ----------
    // A whiff now plays the FULL connect animation (kickLeg.ts `whiff`): same
    // pokeDuration, pokeExtendFrac, pokeHoldFrac and extent 1. These three
    // described the old short, hold-less, half-extent stab, which read as a
    // dropped input rather than a miss. Nothing reads them; they document the
    // previous shape and are the values to restore if the whiff ever needs its
    // own envelope again.
    whiffDuration: 0.16, // s — (unused) old shorter, weaker whiff poke
    whiffHoldFrac: 0.0, // (unused) old whiff never held
    whiffExtent: 0.5, // (unused) old fraction of full reach on a whiff
    // Positions below are the leg model's CENTRE (knee), camera-local. The
    // model is 1.1 m long along Z about its centre; at scale 2.5 that is a
    // 2.75 m stylised leg, so foot/hip sit ±1.375 m (= halfLen) from the
    // centre along the tilted axis. The leg is a CAMERA child, so the rig's
    // 7° pitch never applies to it and the projection is the plain
    // camera-local one: NDC y = (y / -z) / tan(39°), tan(39°) ≈ 0.8098.
    //
    // Poke wrapper rotation.x = θ lifts the shoe end (model -Z after the 180°
    // LEG_YAW), so relative to the knee centre (Y, Z):
    //   shoe = (Y + halfLen·sinθ,  Z - halfLen·cosθ)
    //   hip  = (Y - halfLen·sinθ,  Z + halfLen·cosθ)
    // At full extension θ = pokeTiltRad = 0.8: halfLen·sin = 0.986,
    // halfLen·cos = 0.958. Choosing extendY = -0.986 puts the shoe at
    // camera-local y = 0.000 exactly → NDC y = 0.000, dead centre of the
    // frame vertically, at z = -2.508 m (big in frame). The hip end sits at
    // y = -1.972, z = -0.592 → NDC y = -4.11, i.e. four screen-heights below
    // the bottom edge; swept over the whole envelope its highest value is
    // still -4.11, so the hip NEVER enters frame and stays in front of the
    // camera (z < 0) throughout.
    restY: -3.0, // knee rest position (camera-local, whole leg below frame)
    extendY: -0.986, // peak extension Y — shoe at NDC y = 0, hip at NDC y = -4.1
    // Rest depth. Pushed back from -1.9 to -2.4 when the leg became a two-bone
    // rig: the chamber pose (knee folded UP) pulls the knee back toward the hip
    // and therefore toward the camera, and at z ≈ -1 a knee at any sane height
    // projects far below the bottom edge. Parking rest deeper gives the chamber
    // (which interpolates rest → extend by `chamberLift`/`chamberReach`) room to
    // sit ~2 m out, where the raised knee actually lands in frame. Rest is
    // invisible, so the only constraints are: hip in front of the camera, and
    // nothing on screen at t=0 (verified in pokeReach.test.ts / juice.test.ts).
    restZ: -2.4,
    extendZ: -1.55, // peak extension depth (toward the contact point)
    pokeTiltRad: 0.8, // rad — swings the foot UP as the leg extends
    // Nearer contacts shorten the poke slightly (depth aim), but never enough
    // to pull the shoe away from mid-frame: at this floor the shoe still
    // projects to NDC y ≈ -0.07, inside the ±0.1 mid-frame band, so EVERY
    // connecting kick reads as centre-screen.
    minExtent: 0.95, // fraction of full reach at point-blank contact
    // --- AIMING AT THE TARGET (owner playtests 2026-08-01, 2026-08-02) ------
    // 2026-08-01: "The leg fires straight ahead — make sure the kick actually
    // lands ON the vehicle." 2026-08-02, after that pass shipped: "On double
    // kick, the legs aren't aiming at each vehicle's centre of mass. Both legs
    // extend forward. They should extend OUTWARDS toward the vehicles' centre
    // of mass. (Same with single leg kick.)"
    //
    // THREE THINGS WERE WRONG (see `legAimYaw` and kickLeg.kick):
    //   1. SIGN. `group.rotation.y` was set to +bearing. In three, Ry(+φ) maps
    //      the model's forward axis (0,0,-1) to (-sinφ, 0, -cosφ) — i.e. a
    //      POSITIVE yaw turns the shoe to -X. The leg therefore swung AWAY
    //      from its target; on a double kick the two legs crossed inward.
    //   2. TARGET. The aim used the CONTACT point, which sits on the near face
    //      and half a half-width back toward the player: for the straddle
    //      double kick (lane centres 1.35 m to either side) the contact is
    //      ~0.89 m off axis at 3-4 m of depth — a 13-17° bearing, i.e. visually
    //      forward. The aim now uses the vehicle's CENTRE OF MASS
    //      (kick.ts KickTarget.aimX/aimZ); the dust burst still fires at the
    //      contact, which is where the foot physically lands.
    //   3. CLAMP. 0.42 rad (24°) capped even the honest bearing, and with the
    //      sign wrong it cancelled the leg's own lateral offset almost exactly
    //      — the straddle double kick put BOTH shoes within 0.05 NDC of frame
    //      centre, which is literally "both legs extend forward".
    //
    // The yaw magnitude is SCREEN-ALIGNED rather than a raw bearing: the shoe
    // ends up ~`aimShoeDist` from the camera, so to sit on the camera→target
    // ray it must occupy lateral |dx|·aimShoeDist/depth, which the wrapper
    // delivers by yawing asin(that / aimShoeArm). See `legAimYaw`. Any target
    // a lane or more to the side saturates the clamp; a same-lane target still
    // pokes dead ahead.
    //
    // AIM PIVOT — THE 2026-08-02 "CROSSED LEGS" FIX. Owner playtest, after the
    // sign fix above shipped: "The double kick legs are crossed, not V-shaped."
    // They were. `group.rotation.y` yaws the wrapper about its ORIGIN, and the
    // wrapper origin is the leg model's CENTRE (the knee) — so every radian the
    // shoe swung outward swung the HIP the same amount INWARD, straight across
    // the body. Measured in the bench at the straddle double kick (both legs at
    // the old 0.6 clamp), camera-local, projected:
    //     left  leg: hip NDC x +0.84, knee -0.37, shoe -0.92
    //     right leg: hip NDC x -0.84, knee +0.37, shoe +0.92
    // Each leg ran diagonally from a hip on the OTHER side of the screen to a
    // shoe on its own — two crossed diagonals whose intersection sits just
    // under the bottom edge, with the thighs overlapping across frame centre.
    // The shoes splayed correctly (which is why the shoe-offset unit tests and
    // the previous pass's screenshots both read as a "V"); the legs still
    // crossed.
    //
    // The pivot is now the HIP: `updateLeg` translates the wrapper by
    // `legAimHipShiftX(yaw)` — exactly the displacement the yaw gave the hip,
    // undone — so the hip stays parked at its base offset and the whole leg
    // rotates about it. Same measurement after the fix:
    //     left  leg: hip -0.11, knee -0.64, shoe -0.77   (monotonically out)
    //     right leg: hip +0.11, knee +0.64, shoe +0.77
    //
    // WHY THE CLAMP IS 0.20 AND NOT MORE. Pivoting at the hip DOUBLES the
    // shoe's lateral travel for the same yaw (the shoe swings a full leg-length
    // from a fixed hip instead of half a leg-length from a fixed knee), and the
    // arm it swings on is the full `aimShoeArm` = 1.375 m, so the old 0.6 now
    // throws both shoes clean out of frame. Swept in the bench at 540x960
    // (portrait — the tightest aspect) at mid-hold, over a forced straddle
    // double kick AND a forced lane-away single (which sits ~0.1 NDC wider,
    // being a full lane out): 0.32 leaves the legs hugging the frame edges with
    // the shoes half cut and the middle of the screen empty, 0.26 still grazes
    // the outer shoe on the double (NDC x -0.78) and cuts the single's, 0.20 is
    // the widest splay with every shoe WHOLE — double at NDC x ±0.6, single at
    // ±0.72 — reading as a V out of the bottom centre.
    aimYawMax: 0.20, // rad (11°) — the splay on any off-lane target
    /** Camera-local distance of the shoe at full extension (m). */
    aimShoeDist: 2.5,
    /**
     * Lateral arm the aim yaw acts on (m) — the FULL model half-length, 1.1 m
     * of leg at `scale` 2.5, halved.
     *
     * Not `halfLen·cos(pokeTiltRad)` (0.958), as this was until 2026-08-02. The
     * wrapper's Euler order is the default XYZ, i.e. R = Rx(tilt)·Ry(yaw): the
     * YAW IS APPLIED FIRST, in the untilted frame, and the tilt that follows is
     * a rotation about x, which leaves x alone. The lateral arm is therefore
     * the whole half-length, whatever the tilt — verified by composing the real
     * node transforms (kickLegAim.test.ts). Both `legAimShoeOffsetX` and
     * `legAimHipShiftX` use it, so the hip shift cancels the hip's yaw swing
     * EXACTLY and the pivot lands on the hip.
     */
    aimShoeArm: 1.375,
    /** Depth floor for the aim bearing (m) — never divide by ~0. */
    aimDepthMin: 1.0,
    /**
     * Lateral SLIDE of the whole leg toward the target, as a multiplier on the
     * clamped aim direction (m at full thrust). Was 0.3 when the slide was the
     * only outward cue; halved now that the yaw does the aiming, because both
     * push the shoe the same way and together they walked it off the frame
     * edge on a full-lane-width single kick.
     */
    aimSlide: 0.15,
    /**
     * Maximum foot reach ahead of the camera (m) — the denominator of the
     * extension scale.
     *
     * The leg is 1.1 m of model at `scale` 2.5 = 2.75 m from hip to shoe, and
     * the shoe at full extension sits ~2.5 m in front of the camera. The kick
     * WINDOW, meanwhile, is 4.5 m at run start and 9.1 m at full ramp
     * (TUNING.kick.windowAheadTime), so the target is ALWAYS beyond full reach:
     * geometrically the foot can never touch the bumper of a car the sim
     * considers kickable.
     *
     * THE VISUAL COMPROMISE, decided here: the extension is
     * `clamp(minExtent, 1, contactDistance / reachMax)`, i.e. every real kick
     * plays at FULL extension (the leg is never hyper-extended past its rig,
     * which would look broken), the leg AIMS at the contact, and the contact
     * itself is sold by the dust/spark burst that fires at the vehicle's
     * surface on the connect frame plus the camera dip. Shortening the poke to
     * "honestly" fall short of the car would only make the kick look feeble.
     * The clamp exists so the term still behaves if the kick window is ever
     * tightened to inside 2.75 m — then, and only then, the foot stops short.
     */
    reachMax: 2.75, // m
    // --- Chamber → thrust (REAL knee) ---------------------------------------
    // The leg is now a two-bone FK rig (assets/models/leg_rig.glb: `kick_thigh`
    // with `kick_shin` as its child at the knee), so the knee is a real joint
    // instead of a wrapper-tilt fake. The extend phase is still split in two:
    //   chamber — the THIGH swings up (knee drives to NDC y ≈ -0.27, the lower
    //     third of the portrait frame) while the SHIN folds back ~112° and
    //     twists slightly outward, dropping the calf and its white sock to the
    //     bottom edge, clear of the thigh. Unmistakably a bent knee.
    //   thrust  — both joints unwind to 0 (a straight leg, geometrically
    //     identical to the old rigid GLB) as the wrapper snaps to the extend
    //     position, so the shoe still lands at NDC y ≈ 0.
    // HOLD is a straight leg, exactly as before; the retract TUCKS the knee
    // hard and flattens the leg before dropping it out of frame (see the
    // retract block below for why).
    chamberFrac: 0.4, // fraction of the EXTEND phase spent chambering
    // The wrapper stays nearly LEVEL through the chamber (0.19 × pokeTiltRad):
    // the knee lift is the thigh joint's job now. The old rig over-tilted the
    // whole plank to 1.3× here to fake it; doing both at once shoves the hip up
    // into the near plane and throws the knee off the bottom of the screen.
    chamberTiltMult: 0.19, // × pokeTiltRad at the top of the chamber
    chamberLift: 0.84, // fraction of the way to extendY at the chamber peak
    chamberReach: 0.12, // fraction of the way to extendZ at the chamber peak
    // Joint angles at the chamber peak, in kick_thigh / kick_shin local radians
    // (see pokePose for the sign convention: +rotation.x rotates +Y toward +Z
    // about the joint, so the thigh is driven NEGATIVE to raise the knee).
    // All three were tuned ON SCREEN in the portrait frame (measured NDC at the
    // chamber peak: knee y ≈ -0.27, shoe y ≈ -0.88, hip y ≈ -1.74).
    chamberThighRad: 0.8, // knee-up swing at the hip (~46°)
    chamberKneeRad: 1.95, // knee flexion at the chamber peak (~112°)
    // Tibial yaw on the folded shin. Without it the calf folds straight down
    // the view axis and disappears BEHIND the thigh's huge near-camera mass —
    // the fold is geometrically there but invisible, which is exactly the
    // "it just raises up" complaint. 16° of outward twist swings the calf and
    // its white sock clear of the thigh silhouette, so the bend reads.
    chamberShinYawRad: 0.28,
    // --- Retract (DROP + FOLD + FADE) ---------------------------------------
    // WHY THIS IS NOT JUST "ease everything back to 0".
    //
    // The road is an OPAQUE plane at world y = 0, the camera sits at
    // cameraHeight (1.35 m) pitched 7° down, and the leg is a camera child — so
    // a leg point is drawn only while
    //   worldY = 1.35 + y·cos7° − z·sin7° > 0.
    // Work that through and the bottom edge of the frame (NDC y = −1) is only
    // ABOVE the asphalt within ~1.46 m of the camera. At the shoe's hold depth
    // (2.46 m) the road already covers the whole frame below NDC y ≈ −0.55.
    // There is simply nowhere for a leg at that depth to "drop out of frame":
    // the asphalt gets there first.
    //
    // The old retract ran lift/reach/tilt back down ONE easeInCubic ramp, which
    // hangs the leg at full extension for over half the retract and then sinks
    // it, nearly straight, in place. The road ate it from the hip end upward
    // while the (tilted-up, furthest, therefore highest) shoe stayed above the
    // asphalt longest — leaving the shoe floating in mid-frame with the calf and
    // thigh gone. That is the reported "leg appears clipped out".
    //
    // Three changes, tuned against a road-occlusion solver run over the ACTUAL
    // leg_rig.glb vertices in the browser (see the retract suites in
    // juice.test.ts for the pinned invariant):
    //   1. DROP PROMPTLY. `retractDropExp` replaces the easeInCubic hang, so the
    //      leg is low in frame before the road can matter.
    //   2. FLATTEN AND FOLD. The tilt unwinds fast (`retractTiltExp`) and the
    //      shin folds hard (`retractKneeRad` over `retractFoldFrac`), so the
    //      shoe stops being the highest thing on screen: the leg meets the road
    //      all at once instead of leaving a floating extremity. This drops the
    //      worst floating-island height from ~0.8 of a half-frame to <0.1.
    //   3. FADE. The residual is small but not zero — it cannot be, because the
    //      approved HOLD pose itself sits only ~0.1 of a half-frame above its own
    //      road cut. So the leg also fades out over `retractFadeFrac` of the
    //      retract, and is fully gone before the leftover cut could register.
    /** Exponent on lift = 1 - s^exp — the wrapper's fall to rest height. */
    retractDropExp: 1.6,
    /** Exponent on tilt = (1 - s)^exp — bigger = flattens toward the road sooner. */
    retractTiltExp: 2.6,
    /** Exponent on reach = 1 - s^exp — the pull back to rest depth. */
    retractReachExp: 0.8,
    /** Fraction of the retract over which the shin reaches its full fold. */
    retractFoldFrac: 0.3,
    /** Knee flexion at the end of the retract fold (rad) — deeper than the chamber. */
    retractKneeRad: 2.4,
    /** Fraction of the retract over which the leg fades from opaque to gone. */
    retractFadeFrac: 0.4,
    /**
     * Base lateral offset per leg (camera-local x) — the rider's HIP, which is
     * also the yaw pivot (see the AIM PIVOT block above and `legAimHipShiftX`).
     *
     * Halved from 0.28 in the 2026-08-02 crossed-legs fix. The hip sits ~0.93 m
     * from the camera at full extension, so a metre of hip offset is worth
     * ~2.3 NDC of screen: with the pivot moved to the hip, 0.28 threw the whole
     * leg out to the frame edge and left the middle of the screen empty (swept
     * in the bench at 540x960). 0.16 roots the V at the bottom CENTRE, which is
     * where a first-person rider's hips actually are.
     */
    lateralOffset: 0.16,
    cameraDipDeg: 0.7, // additive camera pitch dip on connect
    cameraDipDuration: 0.16, // s
    scale: 2.5, // uniform leg model scale (big, prominent shoe)
  },

  // --- Score popups ---------------------------------------------------------
  popups: {
    poolSize: 16,
    duration: 0.7, // s — rise + fade
    risePx: 48, // pixels upward travel
    kickCarText: "+50",
    kickTruckText: "+150",
    nearMissText: "+25 NEAR MISS",
    /**
     * DOUBLE KICK banner — spawned ONCE per double-kick sim event, on top of
     * the two per-vehicle score popups, midway between the two contacts.
     */
    doubleKickText: "DOUBLE KICK!",
    /** Hotter than `colorKick` so the banner reads as the rarer event. */
    colorDoubleKick: "#ff7a2f",
    font: "bold 18px ui-monospace, 'SFMono-Regular', Menlo, monospace",
    colorKick: "#ffd24a",
    colorNearMiss: "#7ad7ff",
  },

  // --- Particles ------------------------------------------------------------
  particles: {
    // Sized for the ground-impact plume (up to ~56 particles) landing on top of
    // a running WRECK TRAIL and a kick burst without recycling live sprites.
    // One sliding wreck carries ~110 live particles at full emission rate (see
    // the wreck-trail block below), so 512 covers two simultaneous skids plus
    // both plumes; the pool recycles oldest-first, so overflow degrades rather
    // than breaks.
    // DUST PASS (2026-08-02): 512 -> 768. The impact plume went 52 -> 84 quads
    // and every sliding wreck now also lays a SCRAPE dust trail
    // (`wreckScrapeCount`) on top of its smoke/chips/sparks, which takes one
    // wreck's steady-state load from ~110 to ~170 live particles. 768 keeps the
    // design margin the old number had: two simultaneous skids plus both plumes
    // without recycling a live sprite. Pooled and pre-allocated as before — the
    // only cost is the two instance buffers, which are sized once at load.
    poolSize: 768,
    // --- KICK CONTACT BURST -------------------------------------------------
    // The "the boot really hit that car" cue, fired at the sim event's contact
    // point (kick.ts KickTarget.contactX/contactZ) on the connect frame — never
    // tracked per frame, so it is pinned to where the impact actually happened.
    // Owner brief (2026-08-01): a COMPACT puff of soft dust plus a few sparks.
    // Dust is at the top of the requested 6-10 band because the contact is
    // 4.5-9 m away, where a smaller puff is a couple of pixels; sparks are cut
    // from 14 to 8 rather than to the requested 3-4 because they are the
    // additive layer that survives at that distance and against a bright
    // biome — halved, the burst is visibly a puff-with-glints rather than the
    // old firework.
    kickSparkCount: 8,
    kickDustCount: 10,
    /**
     * Height (m) of the kick contact burst — mid-body on a car, i.e. where a
     * boot meets a door. Also anchors the score popup's projection.
     */
    kickContactY: 0.7,
    /**
     * Sprite size (m) of the CONTACT dust, overriding the generic `dustSize`.
     *
     * The contact is 4.5-9 m out — three to six times further than any other
     * dust in the game — and at 0.5 m the puff measured a couple of dozen
     * pixels behind an extended leg that covers exactly that part of the frame
     * (verified in-browser: the burst was there but read as sparkle, not
     * impact). 0.9 m puts the puff at roughly the width of the car's wheel arch,
     * so it spills out from behind the shoe.
     */
    kickDustSize: 0.9,
    downDustCount: 22,
    crashDebrisCount: 28,
    sparkLifetime: 0.35, // s
    dustLifetime: 0.6, // s
    debrisLifetime: 0.8, // s
    sparkSpeed: 6, // m/s radial
    dustSpeed: 2.5, // m/s
    debrisSpeed: 8, // m/s
    sparkSize: 0.12, // m sprite scale
    dustSize: 0.5, // m
    debrisSize: 0.18, // m
    gravity: 9.81, // m/s^2 downward on debris
    // Canvas-generated sprite (no external texture).
    spritePx: 64,

    // --- Roadside dust (kicked-vehicle ground impact + slide trail) ---------
    // Reference footage: the instant the wreck touches down it throws a big,
    // wide, SLOW plume that hangs, then keeps trailing dust while it skids.
    // Gravity is pulled far below the debris value so the cloud lingers, and
    // the directions come from an upper hemisphere biased outward (see
    // particles.ts sampleDustDirection) rather than a full sphere.
    // DUST PASS (owner playtest 2026-08-02: "increase the dust kicked up when a
    // vehicle crashes"). The plume is the headline beat of the whole kick, so
    // every axis of it was widened at once rather than only the count: 52 -> 84
    // quads, each 1.1 -> 1.4 m and living 1.15 -> 1.45 s base, thrown 5.5 ->
    // 6.8 m/s so the cloud spreads to roughly a lane and a half before it
    // starts to dissipate. Budget: 84 quads against a pool raised to 768 (see
    // poolSize) — the plume is now ~11% of the pool instead of ~10%, so a
    // double kick landing on top of two running scrape trails still fits.
    groundHitCount: 84, // particles per impact plume
    groundHitSpeed: 6.8, // m/s radial
    groundHitSize: 1.4, // m — much larger than sparks/kick dust
    groundHitLifetime: 1.45, // s base (×0.7..1.3 jitter → ~1.0-1.9 s)
    groundHitGravity: 0.35, // m/s^2 — dust hangs instead of falling
    groundHitY: 0.35, // m — emitted just off the road surface
    // --- WRECK TRAIL (the sliding-wreck smoke + debris) ---------------------
    // The kicked wreck now lands hard (TUNING.kick.gravityScale) and SKIDS
    // backward for seconds on a near-frictionless collider
    // (TUNING.kick.wreckFriction). One landing puff cannot carry that: the trail
    // is emitted continuously from the ground hit until the wreck drops below
    // `trailMinSpeed`, in three layers per emission —
    //   SMOKE  — thick, dark grey, large, NEGATIVE gravity so it lifts and
    //            billows behind the wreck (soft/normal-blended batch).
    //   CHIPS  — small dark ballistic fragments (soft batch too: a dark chip on
    //            the ADDITIVE batch is mathematically invisible). Drawn with the
    //            "chip" life curve — near-opaque and SHRINKING, so they read as
    //            solid matter rather than as more dust.
    //   SPARKS — hot orange glints on the additive batch, short-lived and
    //            ballistic: metal on asphalt.
    // The one-shot `groundHit*` plume stays as the landing thud accent.
    wreckSmokeCount: 2,
    wreckSmokeSpeed: 2.4, // m/s radial
    wreckSmokeSize: 1.35, // m — larger than the impact plume's motes
    wreckSmokeLifetime: 1.4, // s
    /** NEGATIVE = buoyant: smoke rises out of the wreck's wake. */
    wreckSmokeGravity: -0.5,
    /**
     * Smoke emission height, as a fraction of the wreck's LIVE body-centre
     * height, clamped to [wreckSmokeYMin, wreckSmokeYMax]. A wreck tumbling on
     * its corners carries its centre 2-3 m up (a semi on its roof sits at 2 m
     * before any tumble), and smoke pinned to the asphalt visibly detached from
     * it. Following the body keeps the plume wrapped around the wreck while the
     * clamp stops it from ever floating in mid-air or sinking under the road.
     */
    wreckSmokeYFrac: 0.7,
    wreckSmokeYMin: 0.5, // m
    wreckSmokeYMax: 2.6, // m
    /** Thick exhaust-grey palette (much darker than the sandy road dust). */
    smokeColorDark: [0.1, 0.1, 0.11],
    smokeColorGrey: [0.29, 0.29, 0.31],
    wreckChipCount: 1,
    wreckChipSpeed: 5.5, // m/s
    wreckChipSize: 0.13, // m
    wreckChipLifetime: 0.7, // s
    chipColorDark: [0.07, 0.07, 0.08],
    chipColorRust: [0.21, 0.14, 0.1],
    wreckSparkCount: 2,
    wreckSparkSpeed: 7.5, // m/s
    wreckSparkSize: 0.1, // m
    wreckSparkLifetime: 0.28, // s
    /** Over-bright additive orange — clips to a hot core (see kindConfig). */
    sparkColorHot: [2.0, 0.85, 0.18],
    /** Emission height (m) for chips + sparks — struck off the road surface. */
    wreckDebrisY: 0.2,
    // --- SCRAPE DUST (the fourth trail layer) -------------------------------
    // Owner playtest (2026-08-02): "increase the dust kicked up when a vehicle
    // crashes." The landing plume was the only DUST in the effect: everything
    // the wreck threw afterwards was dark smoke, dark chips and orange sparks,
    // so a wreck tumbling for 6 m left a black smear and no ground contact read
    // at all. Scrape dust is the sandy road grit ploughed up at the contact
    // patch — the SAME palette, batch and material as the impact plume
    // (`roadDustConfig`), so it adds no pipeline and no new warm-up subject; it
    // is simply that config at trail scale, emitted at the road surface.
    //
    // Emitted only while the wreck is grounded and above
    // `JUICE.particles.trailMinSpeed` — the coordinator's existing gate — and
    // scaled by the same `speedFrac`, so the grit sprays from a fast scrape and
    // dies with the wreck.
    wreckScrapeCount: 3,
    wreckScrapeSpeed: 4.2, // m/s radial (ploughed out sideways, not thrown up)
    wreckScrapeSize: 0.85, // m — between the kick dust and the impact plume
    wreckScrapeLifetime: 0.95, // s
    /** Near-zero gravity: the grit hangs in the wreck's wake like the plume. */
    wreckScrapeGravity: 0.5,
    /** Emission height (m) — right at the asphalt, where the body is grinding. */
    wreckScrapeY: 0.18,
    /** Emissions per second at (and below) `trailMinSpeed` — the floor rate. */
    trailRateMin: 7,
    /** Emissions per second once the wreck is at `trailSpeedRef` or faster. */
    trailRateMax: 26,
    /** Speed (m/s) at which the emission rate saturates at `trailRateMax`. */
    trailSpeedRef: 30,
    /** Below this ground speed (m/s) the wreck stops trailing entirely. */
    trailMinSpeed: 3,
    /** Hard cap on trail emissions across ALL wrecks in one frame. */
    trailMaxPerFrame: 6,
    // Sandy-grey palette: each particle lerps between a tan and a road grey.
    // These are the TRUE colours: dust is drawn on a NORMAL-blended instanced
    // mesh (see particles.ts), so it reads as sandy-grey matter. They used to
    // be scaled to ~0.4 brightness because dust shared the sparks' ADDITIVE
    // material, where full-brightness dust looked like glowing smoke.
    dustColorTan: [0.725, 0.635, 0.478], // #b9a27a
    dustColorGrey: [0.604, 0.604, 0.573], // #9a9a92
    /** Peak opacity of a soft (normal-blended) dust quad — see particleAlpha. */
    softMaxAlpha: 0.42,
  },

  // --- Speed lines ----------------------------------------------------------
  speedLines: {
    thresholdFrac: 0.7, // fraction of speedMax before lines appear
    maxOpacity: 0.35, // at speedMax
    streakCount: 7, // per side
    minLenVh: 12, // min streak length in vh units
    maxLenVh: 35, // max streak length (scales with speed)
  },

  // --- Crash vignette -------------------------------------------------------
  crash: {
    vignetteFadeIn: 0.12, // s — how fast the red vignette reaches full
    vignetteMaxOpacity: 0.55,
  },
} as const;

// ---------------------------------------------------------------------------
// Kick-leg aiming (pure — unit-tested in juice.test.ts).
// ---------------------------------------------------------------------------

/**
 * Yaw (rad) to write to the kick leg's WRAPPER `rotation.y` so the shoe thrusts
 * toward a target at `dxLocal` metres to the side (positive = the player's
 * right) and `targetZ` metres of sim z (negative = ahead). Pass the target
 * vehicle's CENTRE OF MASS, not the contact point (see the aim block in
 * JUICE.leg).
 *
 * THE ONE SIGN CONVENTION FOR KICK-LEG AIMING (read this before touching any
 * of it; every past bug here was a sign or a pivot, never the magnitude):
 *
 *  1. `dxLocal` is camera-local metres, POSITIVE to the player's RIGHT. It is
 *     always the target's x MINUS the player's x — the legs are camera
 *     children and the rig tracks the player, so the player, not the world
 *     origin, is the frame of reference (juiceCoordinator passes
 *     `aimX - state.player.x`).
 *  2. ROTATION. three's Ry(+φ) sends the model's forward axis (0,0,-1) to
 *     (-sinφ, 0, -cosφ): a POSITIVE `rotation.y` points the shoe LEFT. So a
 *     target on the right (dxLocal > 0) returns a NEGATIVE yaw. The shoe's
 *     resulting lateral travel is `legAimShoeOffsetX(yaw) = -arm·sin(yaw)`,
 *     which therefore always carries the sign of `dxLocal`.
 *  3. MIRRORING IS NOT A FACTOR. The right leg's model root carries
 *     `scale.x = -1` (kickLeg `buildLeg`), but that mirror lives on the INNER
 *     clone, one level BELOW the wrapper this yaw is written to. World =
 *     T_wrap · Ry(yaw) · Ry(π) · S_mirror, so the mirror never conjugates the
 *     aim rotation and BOTH legs yaw the same way in camera space for the same
 *     `rotation.y`. (It does flip the rig's own `shinYaw`, which is what makes
 *     the chamber twist mirror for free.) There is no per-leg sign anywhere in
 *     the aim path — one leg pointing across the body means the wrong TARGET
 *     was paired with it, not the wrong sign.
 *  4. PIVOT. `rotation.y` turns the wrapper about the model's CENTRE (the
 *     knee), which swings the hip the opposite way. kickLeg cancels that with
 *     `legAimHipShiftX(yaw)` so the pivot is the HIP and the leg never crosses
 *     the body. See the AIM PIVOT block in JUICE.leg.
 *
 * MAGNITUDE. Not the raw bearing: the shoe stops ~`aimShoeDist` from the
 * camera while the car is 3-9 m out, so matching the bearing would leave the
 * shoe far inboard of the car ON SCREEN. Instead the shoe is placed on the
 * camera→target ray at its own distance — lateral |dx|·aimShoeDist/depth —
 * which the wrapper reaches by yawing asin(lateral / aimShoeArm), clamped to
 * `aimYawMax`. Monotonic in |dx|, exactly 0 for a same-lane target, saturated
 * for anything a lane away.
 */
export function legAimYaw(dxLocal: number, targetZ: number): number {
  const L = JUICE.leg;
  const depth = Math.max(L.aimDepthMin, -targetZ);
  const wantLateral = (Math.abs(dxLocal) * L.aimShoeDist) / depth;
  const sin = Math.min(1, wantLateral / L.aimShoeArm);
  const mag = Math.min(L.aimYawMax, Math.asin(sin));
  return dxLocal >= 0 ? -mag : mag;
}

/**
 * Lateral (camera-local x) offset of the SHOE relative to the leg wrapper for a
 * given aim yaw — the geometric consequence of `legAimYaw`, exposed so tests
 * (and future tuning) can assert the foot actually travels toward the target
 * without instantiating three.
 */
export function legAimShoeOffsetX(yaw: number): number {
  return -JUICE.leg.aimShoeArm * Math.sin(yaw);
}

/**
 * Lateral (camera-local x) shift kickLeg must add to the leg WRAPPER so the aim
 * yaw pivots at the HIP instead of at the model centre — the fix for the
 * crossed double kick (see the AIM PIVOT block in JUICE.leg).
 *
 * The wrapper origin is the leg's middle, so `Ry(yaw)` moves the hip by
 * `+arm·sin(yaw)` — mirror-image of what it does to the shoe. Translating the
 * wrapper by the NEGATIVE of that (which is numerically the same as the shoe's
 * own offset) parks the hip back where it started and turns the whole leg about
 * it: the hip keeps its side of the body, and the shoe's travel doubles.
 */
export function legAimHipShiftX(yaw: number): number {
  return -JUICE.leg.aimShoeArm * Math.sin(yaw);
}

// ---------------------------------------------------------------------------
// Easing helpers (pure, no allocation).
// ---------------------------------------------------------------------------

/** fast-out: decelerating ease for the extend phase. */
export function easeOutCubic(t: number): number {
  const u = 1 - t;
  return 1 - u * u * u;
}

/** ease-in: accelerating ease for the retract phase. */
export function easeInCubic(t: number): number {
  return t * t * t;
}

/**
 * Kick-poke reach envelope: extend → HOLD → retract, as a pure function of
 * normalized time. Returns 0..1 (0 = fully retracted, 1 = fully extended).
 *
 *   t < extendFrac                 : easeOutCubic ramp 0→1 (fast out)
 *   extendFrac ≤ t < ext+hold      : 1 (held at full extension — readability)
 *   t ≥ ext+hold                   : easeInCubic ramp 1→0 (retract)
 *
 * @param t          normalized poke time (clamped to 0..1).
 * @param extendFrac fraction of the poke spent extending.
 * @param holdFrac   fraction of the poke held at full extension.
 */
export function pokeReach(t: number, extendFrac: number, holdFrac: number): number {
  const u = clamp01(t);
  if (extendFrac > 0 && u < extendFrac) return easeOutCubic(u / extendFrac);
  const holdEnd = extendFrac + holdFrac;
  if (u < holdEnd) return 1;
  const retractSpan = 1 - holdEnd;
  if (retractSpan <= 0) return 1;
  return 1 - easeInCubic((u - holdEnd) / retractSpan);
}

/**
 * A kick-leg pose sample.
 *
 * The first three channels are NORMALIZED multipliers for the camera-local
 * WRAPPER transform:
 *  - `reach`: 0 = restZ, 1 = extendZ (depth toward the contact point),
 *  - `lift`:  0 = restY, 1 = extendY (height in frame),
 *  - `tilt`:  × pokeTiltRad.
 *
 * The last two are ABSOLUTE joint angles in RADIANS for the two-bone rig,
 * assigned straight to `rotation.x` of the matching glTF node:
 *  - `thighRot`: `kick_thigh` (pivot = hip). NEGATIVE raises the knee.
 *  - `shinRot`:  `kick_shin` (pivot = knee, child of the thigh). POSITIVE
 *    folds the calf/foot back under the thigh; magnitude = knee flexion.
 *  - `shinYaw`:  `kick_shin` yaw (rotation.y) — a small outward twist that
 *    keeps the folded calf clear of the thigh's silhouette. Mirrors for free
 *    on the right leg (its clone root carries scale.x = -1).
 * Both are 0 for a straight leg, which reproduces the old rigid-GLB pose
 * exactly — so the hold-phase framing math is unchanged.
 *
 * `alpha` is the leg's OPACITY, 1 for everything except the retract fade (see
 * the retract block in JUICE.leg). kickLeg only turns the material transparent
 * while this is below 1, so the opaque phases keep their exact old rendering.
 */
export interface PokePose {
  reach: number;
  lift: number;
  tilt: number;
  thighRot: number;
  shinRot: number;
  shinYaw: number;
  alpha: number;
}

/**
 * Chamber → thrust → HOLD → retract pose envelope for a CONNECTING kick.
 *
 * `pokeReach` moves depth, height and tilt on one shared curve, which reads as
 * a knee-locked plank swinging up. This drives the two-bone leg rig through the
 * same timing, with a real knee:
 *
 *   chamber (first `chamberFrac` of extendFrac)
 *     The THIGH swings up to `-chamberThighRad` and the SHIN folds to
 *     `+chamberKneeRad` (~92° of knee flexion) while the wrapper only travels
 *     `chamberLift` / `chamberReach` of the way out and stays level
 *     (`chamberTiltMult` = 0): knee HIGH in frame, shoe tucked down under it.
 *   thrust (the remainder of extendFrac)
 *     Both joints unwind to 0 — the leg SNAPS straight — while lift/reach/tilt
 *     go to 1. The shoe shoots from the bottom edge to frame centre.
 *   hold   — (1, 1, 1) with both joints at 0: a straight leg, geometrically
 *     identical to the old rigid GLB, so the shoe still parks at NDC y ≈ 0.
 *   retract — DROP, FOLD and FADE. Every channel runs on its OWN ramp: the
 *     wrapper falls promptly, the tilt unwinds faster than it falls, the shin
 *     folds hard, and `alpha` fades the leg out. Sharing one ramp (as the old
 *     retract did) sank a nearly straight leg at hold depth, where the opaque
 *     road plane ate it from the hip up and left the shoe floating in mid-frame.
 *     See the retract block in JUICE.leg for the geometry and the tuning.
 *
 * Continuous at every phase boundary (both joints pass through 0 at the
 * thrust/hold boundary and at the hold/retract boundary, and every wrapper
 * channel enters the retract at 1). `out` is written in place so callers can
 * keep a scratch object and allocate nothing per frame.
 *
 * @param t          normalized poke time (clamped to 0..1).
 * @param extendFrac fraction of the poke spent extending (chamber + thrust).
 * @param holdFrac   fraction of the poke held at full extension.
 */
export function pokePose(
  t: number,
  extendFrac: number,
  holdFrac: number,
  out: PokePose = {
    reach: 0,
    lift: 0,
    tilt: 0,
    thighRot: 0,
    shinRot: 0,
    shinYaw: 0,
    alpha: 1,
  },
): PokePose {
  const L = JUICE.leg;
  const u = clamp01(t);
  const chamberEnd = extendFrac * L.chamberFrac;
  // Fully opaque everywhere except the retract fade, which overwrites this.
  out.alpha = 1;

  if (extendFrac > 0 && u < chamberEnd) {
    // Chamber: knee up fast, shoe folded low and close.
    const p = easeOutCubic(u / chamberEnd);
    out.tilt = p * L.chamberTiltMult;
    out.lift = p * L.chamberLift;
    out.reach = p * L.chamberReach;
    out.thighRot = -p * L.chamberThighRad;
    out.shinRot = p * L.chamberKneeRad;
    out.shinYaw = p * L.chamberShinYawRad;
    return out;
  }
  if (extendFrac > 0 && u < extendFrac) {
    // Thrust: the knee SNAPS straight while the wrapper reaches full extension.
    const p = easeOutCubic((u - chamberEnd) / (extendFrac - chamberEnd));
    const q = 1 - p;
    out.tilt = L.chamberTiltMult + (1 - L.chamberTiltMult) * p;
    out.lift = L.chamberLift + (1 - L.chamberLift) * p;
    out.reach = L.chamberReach + (1 - L.chamberReach) * p;
    out.thighRot = -L.chamberThighRad * q;
    out.shinRot = L.chamberKneeRad * q;
    out.shinYaw = L.chamberShinYawRad * q;
    return out;
  }

  const holdEnd = extendFrac + holdFrac;
  const retractSpan = 1 - holdEnd;
  if (u < holdEnd || retractSpan <= 0) {
    out.tilt = 1;
    out.lift = 1;
    out.reach = 1;
    out.thighRot = 0;
    out.shinRot = 0;
    out.shinYaw = 0;
    return out;
  }
  // --- RETRACT: drop, fold, fade ------------------------------------------
  // `s` is progress through the retract alone. Every channel starts at the hold
  // value (1 / 1 / 1 / 0 / 0 / 0, alpha 1) so the hold→retract seam is
  // continuous, and the three wrapper channels all land on 0 (= rest) at s = 1.
  //
  // The channels deliberately do NOT share one ramp any more (see the JUICE.leg
  // retract block for the road-plane geometry that forced this):
  //   lift   falls promptly instead of hanging at full extension,
  //   tilt   unwinds FASTER than the fall, flattening the leg toward the road
  //          plane so the shoe stops being the highest thing on screen,
  //   the shin folds hard on its own ramp, pulling the extremity down with it,
  //   reach  eases back to rest depth throughout,
  //   alpha  fades the leg out well before it is home.
  // The THIGH is deliberately left straight: swinging the knee up here piles the
  // folded leg into the near plane, which reads far worse than the bug.
  const s = (u - holdEnd) / retractSpan;
  const fold = clamp01(s / L.retractFoldFrac);
  out.lift = 1 - Math.pow(s, L.retractDropExp);
  out.tilt = Math.pow(1 - s, L.retractTiltExp);
  out.reach = 1 - Math.pow(s, L.retractReachExp);
  out.thighRot = 0;
  out.shinRot = L.retractKneeRad * fold;
  // Twist the folding calf clear of the thigh, as in the chamber. Reaches full
  // twist at half the fold so the bend reads while the leg is still in frame.
  out.shinYaw = L.chamberShinYawRad * Math.min(1, fold * 2);
  out.alpha = 1 - clamp01(s / L.retractFadeFrac);
  return out;
}

/** smooth out-and-back for FOV punch (cosine bell, 0→1→0 over [0,1]). */
export function easeOutAndBack(t: number): number {
  // 0 at t=0, 1 at t=0.5, 0 at t=1 — a sine arch.
  return Math.sin(t * Math.PI);
}

/** Linear clamp. */
export function clamp01(t: number): number {
  return t < 0 ? 0 : t > 1 ? 1 : t;
}

/** Frame-rate-aware exponential decay factor (0..1 per frame). */
export function decayFactor(ratePerSec: number, dt: number): number {
  return 1 - Math.exp(-ratePerSec * dt);
}

/** 1D value-noise-ish function (cheap pseudo-perlin for shake). */
export function noise1D(t: number, seed: number): number {
  // Sum of sines at different frequencies — smooth, bounded [-1,1].
  const s = seed * 12.9898;
  return (
    Math.sin(t * 6.2832 * 1.0 + s) * 0.5 +
    Math.sin(t * 6.2832 * 2.3 + s * 1.7) * 0.3 +
    Math.sin(t * 6.2832 * 4.7 + s * 2.3) * 0.2
  );
}
